// ─── SommSync Shared Styles ───────────────────────────────────────────────────
//
// Style objects that appear in more than one place. Every function takes the
// active theme `t` and returns a plain style object, so nothing here hardcodes a
// colour — change the scheme in theme.js and these follow.
//
// USAGE
//   import { input, label, buttonPrimary } from './styles';
//   const t = useTheme();
//   <input style={input(t)} />
//   <button style={buttonPrimary(t)}>Save</button>
//
// Spread to vary one property:
//   <input style={{ ...input(t), marginBottom: 0 }} />
//
// WHEN TO ADD SOMETHING HERE
//   When the same visual treatment appears in three or more places. A one-off
//   stays inline — pulling every style out would make App.js harder to read, not
//   easier. The point is shared vocabulary, not exhaustive extraction.

// ─── Scale ────────────────────────────────────────────────────────────────────
// Named so spacing and rounding stay consistent as new screens are added.

export const space = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 };
export const radius = { sm: 6, md: 8, lg: 10, xl: 12, pill: 999 };
export const fontSize = { micro: 10, tiny: 11, small: 12, body: 13, lead: 14, title: 16, hero: 22 };

// ─── Form fields ──────────────────────────────────────────────────────────────

export const input = (t) => ({
  width: "100%",
  boxSizing: "border-box",
  background: t.white06,
  border: `0.5px solid ${t.borderMid}`,
  color: t.textPrimary,
  padding: "8px 10px",
  borderRadius: radius.sm,
  fontFamily: t.fontSerif,
  // 16px, not fontSize.small: iOS Safari zooms the whole page when a text field
  // under 16px takes focus, and stays zoomed afterwards. This style is the owner
  // sign-in and Settings fields, so a 12px value here left the dashboard opening
  // zoomed and cut off on an iPhone. See the safety net in public/index.html.
  fontSize: 16,
  outline: "none",
  marginBottom: space.sm,
});

// Larger variant for sign-in and other standalone forms.
export const inputLarge = (t) => ({
  ...input(t),
  background: t.bgInput,
  border: `1px solid ${t.borderStrong}`,
  padding: "11px 12px",
  fontSize: 16, // see input() above: under 16px zooms the page on iOS
  marginBottom: space.md,
});

export const label = (t) => ({
  color: t.textMuted,
  fontSize: fontSize.micro,
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  marginBottom: space.xs,
  display: "block",
});

export const select = (t) => ({
  ...input(t),
  cursor: "pointer",
});

// ─── Buttons ──────────────────────────────────────────────────────────────────

export const buttonPrimary = (t, { disabled = false } = {}) => ({
  background: t.accent,
  color: t.accentText,
  border: "none",
  padding: "9px 16px",
  borderRadius: radius.sm,
  cursor: disabled ? "default" : "pointer",
  fontFamily: t.fontSerif,
  fontSize: fontSize.small,
  fontWeight: 600,
  opacity: disabled ? 0.6 : 1,
});

export const buttonSecondary = (t) => ({
  background: "none",
  border: `0.5px solid ${t.borderMid}`,
  color: t.textDim,
  padding: "9px 16px",
  borderRadius: radius.sm,
  cursor: "pointer",
  fontFamily: t.fontSerif,
  fontSize: fontSize.small,
});

// Small inline action — "Edit", "Change", and the like.
export const buttonGhost = (t) => ({
  background: "none",
  border: `0.5px solid ${t.borderStrong}`,
  color: t.textMuted,
  padding: "5px 12px",
  borderRadius: radius.sm,
  cursor: "pointer",
  fontFamily: t.fontSerif,
  fontSize: fontSize.tiny,
});

export const buttonDanger = (t) => ({
  ...buttonGhost(t),
  color: t.error,
  borderColor: t.errorBorder,
});

// Toggleable pill — restaurant assignment, filters, tabs.
export const chip = (t, active = false) => ({
  background: active ? t.accent : "none",
  border: `0.5px solid ${active ? t.accent : t.borderMid}`,
  color: active ? t.accentText : t.textDim,
  padding: "5px 12px",
  borderRadius: 14,
  cursor: "pointer",
  fontFamily: t.fontSerif,
  fontSize: fontSize.small,
});

// ─── Containers ───────────────────────────────────────────────────────────────

export const card = (t, { highlighted = false } = {}) => ({
  background: t.bgSurface,
  border: `1px solid ${highlighted ? t.accent : t.borderSubtle}`,
  borderRadius: radius.lg,
  padding: "14px 16px",
});

// Bordered section within a settings screen.
export const panel = (t) => ({
  background: t.white06,
  border: `0.5px solid ${t.borderStrong}`,
  borderRadius: radius.lg,
  padding: "14px 16px",
  marginBottom: space.xl,
});

// Small caps heading that opens a panel.
export const panelHeading = (t) => ({
  color: t.accent,
  fontSize: fontSize.micro,
  letterSpacing: "2px",
  textTransform: "uppercase",
  marginBottom: 3,
});

export const helpText = (t) => ({
  color: t.textDim,
  fontSize: fontSize.micro,
  lineHeight: 1.6,
});

// ─── Status messages ──────────────────────────────────────────────────────────

export const errorText = (t) => ({
  color: t.error,
  fontSize: fontSize.small,
  lineHeight: 1.4,
});

export const successText = (t) => ({
  color: t.success,
  fontSize: fontSize.tiny,
});

export const warningBox = (t) => ({
  padding: "8px 10px",
  background: t.warningDim,
  border: `0.5px solid ${t.warning}`,
  borderRadius: radius.sm,
  color: t.warning,
  fontSize: fontSize.tiny,
  lineHeight: 1.5,
});

// ─── Overlays ─────────────────────────────────────────────────────────────────

export const modalBackdrop = (t) => ({
  position: "fixed",
  inset: 0,
  background: t.bgOverlay,
  zIndex: 999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: t.fontSerif,
});

export const modalPanel = (t, width = 320) => ({
  background: t.bgSurface,
  border: `1px solid ${t.borderSubtle}`,
  borderRadius: radius.xl,
  padding: "32px 28px",
  width,
});

// Centred full-page message — loading, blocked, empty states.
export const fullPageMessage = (t) => ({
  minHeight: "100vh",
  background: t.bgBase,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: t.fontSerif,
  color: t.textDim,
  fontSize: fontSize.body,
  padding: space.xl,
  textAlign: "center",
});
